import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * useAutoDraft — debounced auto-save of arbitrary form state to localStorage.
 *
 * - Saves `data` under `key` every `debounceMs` after it changes.
 * - When `key` changes (including from null → string), re-reads the stored
 *   draft and exposes it via `restored`.
 * - `clearDraft()` wipes the saved snapshot.
 *
 * @param {string|null} key       unique localStorage key (null disables the effect)
 * @param {any}         data      current form data (serializable)
 * @param {object} [options]
 * @param {number}  [options.debounceMs=800]
 * @param {boolean} [options.enabled=true]    turn the effect off while loading or after submit
 */
export function useAutoDraft(key, data, { debounceMs = 800, enabled = true } = {}) {
  const [restored, setRestored] = useState(null); // {data, savedAt} | null
  const lastCheckedKey = useRef(null);
  const timerRef = useRef(null);

  // Re-read the draft whenever the key changes (including null → "foo")
  useEffect(() => {
    if (lastCheckedKey.current === key) return;
    lastCheckedKey.current = key;
    if (!key) {
      setRestored(null);
      return;
    }
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && 'data' in parsed) {
          setRestored({ data: parsed.data, savedAt: parsed.savedAt });
        } else {
          setRestored(null);
        }
      } else {
        setRestored(null);
      }
    } catch {
      setRestored(null);
    }
  }, [key]);

  // Debounced save on data change
  useEffect(() => {
    if (!enabled || !key) return;
    if (data === undefined || data === null) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), data }));
      } catch {
        /* quota or disabled — ignore */
      }
    }, debounceMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [key, data, debounceMs, enabled]);

  const clearDraft = useCallback(() => {
    if (!key) return;
    try { localStorage.removeItem(key); } catch {}
    setRestored(null);
  }, [key]);

  return { restored, clearDraft };
}

export default useAutoDraft;
